/* eslint-disable no-undef */
if (!customElements.get('product-media')) {
  class ProductMedia extends HTMLElement {
    constructor () {
      super()
      this.thumbnailClickListeners = []
      this.navigationClickListeners = {}
      this.videoTimeouts = {}
      this.hintTimeouts = {}
      this.resizeTimeout = null
      this._activeVariantId = null
      this._pendingVariantReinit = false
      this._prepareVariantMediaChain = Promise.resolve()
      this._reinitializeChain = Promise.resolve()
      this._bootstrapPromise = null
      this._onVariantChange = this.onVariantChange.bind(this)
      this._boundHandleResize = null
      this._handlePrevClick = this.handlePrevClick.bind(this)
      this._handleNextClick = this.handleNextClick.bind(this)
    }

    connectedCallback () {
      if (this.dataset.productMediaSize === '1') return
      if (!EmblaCarousel) return
      if (this._bootstrapPromise) return
      this.firstTimeLoad = localStorage.getItem(`firstTimeLoad-${this.dataset.productId}`) === null
      this.loading = true
      this.lastViewport = window.innerWidth >= 1280 ? 'desktop' : 'mobile'
      this.addEventListener('variantChange', this._onVariantChange)
      this._boundHandleResize = this.handleResize.bind(this)
      window.addEventListener('resize', this._boundHandleResize)
      this._bootstrapPromise = this.bootstrapCarousel().then(() => {
        this.loading = false
        this.warmVariantFeaturedImages()
        if (this.isCarouselDebug()) {
          console.info('[product-media] carousel debug active — add ?tb_debug_carousel or set window.TB_DEBUG_CAROUSEL = true')
          this.logCarouselDebug('bootstrap-complete')
        }
      })
    }

    warmVariantFeaturedImages () {
      const warm = () => {
        this.querySelectorAll('.product-carousel__viewport--main .product-media__variant-featured img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager'
        })
      }
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(warm, { timeout: 2000 })
      } else {
        setTimeout(warm, 200)
      }
    }

    async bootstrapCarousel () {
      const variantId = this._activeVariantId || String(this.initialVariantId)
      await this.prepareVariantMedia(variantId)
      await this.initCarousel()
      if (this._pendingVariantReinit && this.mainEmbla) {
        this._pendingVariantReinit = false
        await this.reinitializeCarousel()
      }
    }

    async initCarousel () {
      this.mainOptions = {
        speed: 1,
        duration: 1,
        skipSnaps: false,
        dragThreshold: 1,
        containScroll: 'trimSnaps',
        loop: true,
        startIndex: this.getInitialMainIndex(),
        watchDrag: (emblaApi, event) => {
          const progressBarDragging = event.target.closest('product-gallery-video[data-progress-dragging="true"]')
          return !progressBarDragging
        },
      }
      this.thumbOptions = {
        dragFree: true,
        loop: false,
        align: 'center',
        speed: 1,
        duration: 20,
        startIndex: this.getInitialThumbIndex(),
        breakpoints: {
          '(min-width: 1280px)': {
            active: false,
          },
        },
      }
      await this.setupCarousels().then(() => {
        this.setupThumbnailClick()
        if (this.firstTimeLoad) {
          localStorage.setItem(`firstTimeLoad-${this.dataset.productId}`, 'false')
          this.handleFirstTimeLoad()
        }
      })
      if (!document.getElementById('fullscreen-hint-styles')) {
        const style = document.createElement('style')
        style.id = 'fullscreen-hint-styles'
        style.innerHTML = `
          .fullscreen-hint{position:absolute;bottom:calc(100% + 10px);right:0;white-space:nowrap;z-index:40}
          .fullscreen-hint::after{content:'';position:absolute;top:100%;right:14px;border:6px solid transparent;border-top-color:#ffffff}
        `
        document.head.appendChild(style)
      }
    }

    async prepareVariantMedia (variantId) {
      const normalizedVariantId = String(variantId)
      this._activeVariantId = normalizedVariantId
      const run = async () => {
        const variantSlug = this.getVariantSlug(normalizedVariantId)
        this.featuredMedia.forEach((featuredMedia) => {
          featuredMedia.classList.toggle('tb-hidden', featuredMedia.dataset.variantId !== normalizedVariantId)
        })
        this.applyVariantMediaFilter(variantSlug)
        this.removeAdditionalImages()
        const variantImages = this.variantImages[normalizedVariantId] || []
        if (variantImages.length > 0) {
          await this.addAdditionalImages(normalizedVariantId, variantImages)
        }
        this.pauseAllCarouselVideos()
        this.setAttribute('variant-filter-ready', '')
      }
      this._prepareVariantMediaChain = this._prepareVariantMediaChain.then(run, run)
      await this._prepareVariantMediaChain
    }

    getVariantFilterContainer (containerKey) {
      if (containerKey === 'main') {
        return this.querySelector('.product-carousel__container')
      }
      if (containerKey === 'thumb-mobile') {
        return this.querySelector('.product-carousel-thumbs__container-mobile')
      }
      if (containerKey === 'thumb-mobile-overview') {
        return this.querySelector('.product-carousel-thumbs__mobile-overview')
      }
      if (containerKey === 'thumb-desktop') {
        return this.querySelector('.product-carousel-thumbs__container')
      }
      return null
    }

    repairMisplacedMobileThumbSlides () {
      const overview = this.querySelector('.product-carousel-thumbs__mobile-overview')
      const mobileContainer = this.querySelector('.product-carousel-thumbs__container-mobile')
      if (!overview || !mobileContainer) return
      const featuredThumb = mobileContainer.querySelector('.product-carousel-thumbs__slide--featured')
      const misplaced = [...overview.querySelectorAll('.product-carousel-thumbs__slide--variant-filtered:not(.product-carousel-thumbs__slide--overview)')]
      let insertAfter = featuredThumb
      misplaced.forEach((slide) => {
        if (insertAfter) {
          insertAfter.insertAdjacentElement('afterend', slide)
          insertAfter = slide
        } else {
          mobileContainer.appendChild(slide)
          insertAfter = slide
        }
      })
    }

    getVariantFilteredSlides (containerKey, container) {
      if (containerKey === 'thumb-mobile-overview') {
        return [...container.querySelectorAll(':scope > .product-carousel-thumbs__slide--variant-filtered')]
      }
      return [...container.querySelectorAll('.product-carousel__slide--variant-filtered, .product-carousel-thumbs__slide--variant-filtered')]
    }

    insertSlideFromStash (slide, container, containerKey) {
      const beforeMediaId = slide.dataset.stashBeforeMediaId
      const beforeAnchor = slide.dataset.stashBeforeAnchor
      delete slide.dataset.stashContainer
      delete slide.dataset.stashBeforeMediaId
      delete slide.dataset.stashBeforeAnchor

      if (containerKey === 'main' && slide.classList.contains('product-carousel__slide--longform')) {
        const existingLongform = container.querySelector('.product-carousel__slide--longform')
        if (existingLongform) {
          container.insertBefore(slide, existingLongform)
        } else {
          container.appendChild(slide)
        }
        return
      }

      if (beforeMediaId) {
        const beforeEl = container.querySelector(`[data-media-id="${beforeMediaId}"]`)
        if (beforeEl) {
          container.insertBefore(slide, beforeEl)
          return
        }
      }

      if (beforeAnchor === 'featured') {
        const featured = container.querySelector('.product-carousel-thumbs__slide--featured')
        if (featured) {
          container.insertBefore(slide, featured)
          return
        }
      }
      if (beforeAnchor === 'viewport-mobile') {
        const mobileViewport = container.querySelector('.product-carousel-thumbs__viewport-mobile')
        if (mobileViewport) {
          container.insertBefore(slide, mobileViewport)
          return
        }
      }
      if (beforeAnchor === 'longform-main') {
        const longformSlide = container.querySelector('.product-carousel__slide--longform')
        if (longformSlide) {
          container.insertBefore(slide, longformSlide)
          return
        }
      }

      if (slide.classList.contains('product-carousel-thumbs__slide--overview')) {
        if (containerKey === 'thumb-mobile-overview') {
          const mobileViewport = container.querySelector('.product-carousel-thumbs__viewport-mobile')
          if (mobileViewport) {
            container.insertBefore(slide, mobileViewport)
            return
          }
        }
        if (containerKey === 'thumb-desktop') {
          const featured = container.querySelector('.product-carousel-thumbs__slide--featured')
          if (featured) {
            container.insertBefore(slide, featured)
            return
          }
          container.prepend(slide)
          return
        }
      }

      if (containerKey === 'thumb-mobile-overview') {
        const mobileViewport = container.querySelector('.product-carousel-thumbs__viewport-mobile')
        if (mobileViewport) {
          container.insertBefore(slide, mobileViewport)
          return
        }
      }

      const longformSlide = container.querySelector('.product-carousel__slide--longform')
      if (longformSlide && slide.classList.contains('product-carousel__slide')) {
        container.insertBefore(slide, longformSlide)
        return
      }

      container.appendChild(slide)
    }

    restoreVariantSlidesFromStash () {
      const stash = this.variantStash
      if (!stash) return
      const stashedSlides = [...stash.querySelectorAll('.product-carousel__slide--variant-filtered, .product-carousel-thumbs__slide--variant-filtered')]
      stashedSlides.forEach((slide) => {
        const containerKey = slide.dataset.stashContainer
        const container = this.getVariantFilterContainer(containerKey)
        if (!container) return
        this.insertSlideFromStash(slide, container, containerKey)
      })
    }

    moveVariantSlideToStash (slide, containerKey) {
      const stash = this.variantStash
      if (!stash) return
      const nextSibling = slide.nextElementSibling
      delete slide.dataset.stashBeforeMediaId
      delete slide.dataset.stashBeforeAnchor
      if (containerKey === 'main' && slide.classList.contains('product-carousel__slide--longform')) {
        slide.dataset.stashBeforeAnchor = 'longform-main'
      } else if (nextSibling?.dataset?.mediaId) {
        slide.dataset.stashBeforeMediaId = nextSibling.dataset.mediaId
      } else if (nextSibling?.classList?.contains('product-carousel-thumbs__slide--featured')) {
        slide.dataset.stashBeforeAnchor = 'featured'
      } else if (nextSibling?.classList?.contains('product-carousel-thumbs__viewport-mobile')) {
        slide.dataset.stashBeforeAnchor = 'viewport-mobile'
      } else if (nextSibling?.classList?.contains('product-carousel__slide--longform')) {
        slide.dataset.stashBeforeAnchor = 'longform-main'
      }
      slide.dataset.stashContainer = containerKey
      stash.appendChild(slide)
    }

    applyVariantMediaFilter (variantSlug) {
      this.repairMisplacedMobileThumbSlides()
      this.restoreVariantSlidesFromStash()
      const containerConfigs = [
        { key: 'main', selector: '.product-carousel__container' },
        { key: 'thumb-mobile-overview', selector: '.product-carousel-thumbs__mobile-overview' },
        { key: 'thumb-mobile', selector: '.product-carousel-thumbs__container-mobile' },
        { key: 'thumb-desktop', selector: '.product-carousel-thumbs__container' },
      ]
      containerConfigs.forEach(({ key, selector }) => {
        const container = this.querySelector(selector)
        if (!container) return
        const slides = this.getVariantFilteredSlides(key, container)
        slides.forEach((slide) => {
          const tags = (slide.dataset.variantTags || '').split(',').map(tag => tag.trim()).filter(Boolean)
          if (tags.length === 0) return
          if (!variantSlug || !tags.includes(variantSlug)) {
            this.moveVariantSlideToStash(slide, key)
          }
        })
      })
      this.repairMainLongformSlideOrder()
      this.repairThumbSlideOrderFromMain()
      this.logCarouselDebug('variant-filter-applied', {
        mainDomIds: [...(this.querySelector('.product-carousel__container')?.children || [])].map(el => el.dataset.mediaId || 'featured'),
        thumbDomIds: [...(this.querySelector('.product-carousel-thumbs__container-mobile')?.children || this.querySelector('.product-carousel-thumbs__container')?.children || [])].map(el => el.dataset.mediaId || 'featured'),
      })
    }

    repairMainLongformSlideOrder () {
      const container = this.querySelector('.product-carousel__container')
      if (!container) return
      container.querySelectorAll('.product-carousel__slide--longform').forEach((slide) => {
        container.appendChild(slide)
      })
    }

    repairThumbSlideOrderFromMain () {
      const mainContainer = this.querySelector('.product-carousel__container')
      if (!mainContainer) return
      const mainOrder = [...mainContainer.querySelectorAll('.product-carousel__slide')].flatMap((slide) => {
        if (slide.classList.contains('product-carousel__slide--featured')) return []
        if (slide.classList.contains('product-carousel__slide--longform')) return []
        const mediaId = slide.dataset.mediaId
        return mediaId ? [mediaId] : []
      })
      const reorderThumbContainer = (container) => {
        if (!container) return
        const featured = container.querySelector('.product-carousel-thumbs__slide--featured')
        let insertAfter = featured
        mainOrder.forEach((mediaId) => {
          const thumb = container.querySelector(`.product-carousel-thumbs__slide[data-media-id="${mediaId}"]`)
          if (!thumb) return
          if (insertAfter) {
            insertAfter.insertAdjacentElement('afterend', thumb)
            insertAfter = thumb
          } else {
            container.appendChild(thumb)
            insertAfter = thumb
          }
        })
      }
      reorderThumbContainer(this.querySelector('.product-carousel-thumbs__container-mobile'))
      reorderThumbContainer(this.querySelector('.product-carousel-thumbs__container'))
    }

    getVariantSlug (variantId) {
      const map = this.variantSlugMap
      return map[variantId] || ''
    }

    isCarouselDebug () {
      if (window.TB_DEBUG_CAROUSEL === true) return true
      try {
        return new URLSearchParams(window.location.search).has('tb_debug_carousel')
      } catch {
        return false
      }
    }

    getSlideRole (slide) {
      if (!slide) return 'unknown'
      if (slide.classList.contains('product-carousel__slide--featured')) return 'featured'
      if (slide.classList.contains('product-carousel__slide--longform')) return 'longform'
      if (slide.classList.contains('product-carousel__slide--additional-image')) return 'additional-image'
      if (slide.classList.contains('product-carousel__slide--media')) return 'media'
      return 'slide'
    }

    getSlideDebugLabel (slide) {
      if (!slide) return null
      const img = slide.querySelector('img')
      if (img?.alt) return img.alt.slice(0, 80)
      const galleryVideo = slide.querySelector('product-gallery-video')
      if (galleryVideo?.dataset?.alt) return galleryVideo.dataset.alt.slice(0, 80)
      return null
    }

    describeMainSlide (slide, emblaIndex) {
      return {
        emblaIndex,
        relativeIndex: emblaIndex,
        mediaId: slide?.dataset?.mediaId || null,
        variantId: slide?.dataset?.variantId || null,
        variantTags: slide?.dataset?.variantTags || null,
        role: this.getSlideRole(slide),
        label: this.getSlideDebugLabel(slide),
      }
    }

    getVisibleMainSlideIndex () {
      const viewport = this.querySelector('.product-carousel__viewport--main')
      if (!viewport || !this.mainEmbla) return null
      const vpRect = viewport.getBoundingClientRect()
      const slides = this.mainEmbla.slideNodes()
      let best = null
      let bestOverlap = 0
      slides.forEach((slide, i) => {
        const rect = slide.getBoundingClientRect()
        const overlap = Math.max(0, Math.min(rect.right, vpRect.right) - Math.max(rect.left, vpRect.left))
        if (overlap > bestOverlap) {
          bestOverlap = overlap
          best = i
        }
      })
      return best
    }

    buildCarouselDebugSnapshot (context) {
      if (!this.mainEmbla) {
        return { context, error: 'no mainEmbla' }
      }
      const slides = this.mainEmbla.slideNodes()
      const emblaIndex = this.mainEmbla.selectedScrollSnap()
      const visibleDomIndex = this.getVisibleMainSlideIndex()
      const activeVariantId = this._activeVariantId || String(this.initialVariantId)
      const stash = this.variantStash
      return {
        context,
        emblaIndex,
        previousEmblaIndex: this.mainEmbla.previousScrollSnap(),
        visibleDomIndex,
        emblaMatchesVisibleDom: visibleDomIndex === null || visibleDomIndex === emblaIndex,
        thumbIndex: this.thumbsEmbla ? this.getThumbIndexForMainIndex(emblaIndex) : null,
        totalVisibleSlides: slides.length,
        stashedMainSlides: stash ? stash.querySelectorAll('.product-carousel__slide').length : 0,
        activeVariantId,
        variantSlug: this.getVariantSlug(activeVariantId),
        loop: this.mainEmbla.internalEngine?.()?.options?.loop ?? null,
        scrollProgress: this.mainEmbla.scrollProgress(),
        activeSlide: this.describeMainSlide(slides[emblaIndex], emblaIndex),
        visibleDomSlide: visibleDomIndex !== null && slides[visibleDomIndex]
          ? this.describeMainSlide(slides[visibleDomIndex], visibleDomIndex)
          : null,
        visibleSlides: slides.map((slide, index) => this.describeMainSlide(slide, index)),
        thumbDomIds: [...(this.querySelector('.product-carousel-thumbs__container-mobile')?.children || this.querySelector('.product-carousel-thumbs__container')?.children || [])].map(el => el.dataset.mediaId || 'featured'),
        thumbMatchesMainOrder: this.thumbOrderMatchesMain(),
        setupNavigationCount: this._debugSetupNavigationCount || 0,
        nextClickCount: this._debugNextClickCount || 0,
        prevClickCount: this._debugPrevClickCount || 0,
      }
    }

    logCarouselDebug (context, extra = {}) {
      if (!this.isCarouselDebug()) return
      const snapshot = this.buildCarouselDebugSnapshot(context)
      const mainMedia = snapshot.visibleSlides?.filter(s => s.role === 'media').map(s => s.mediaId) || []
      const thumbMedia = (snapshot.thumbDomIds || []).filter(id => id !== 'featured')
      console.groupCollapsed(`[product-media] ${context}`)
      console.log({ ...snapshot, ...extra })
      if (mainMedia.length && thumbMedia.length && JSON.stringify(mainMedia) !== JSON.stringify(thumbMedia)) {
        console.warn('[product-media] thumb strip order ≠ main carousel order', { mainMedia, thumbMedia })
      }
      console.groupEnd()
    }

    thumbOrderMatchesMain () {
      const mainContainer = this.querySelector('.product-carousel__container')
      const thumbContainer = this.querySelector('.product-carousel-thumbs__container-mobile') ||
        this.querySelector('.product-carousel-thumbs__container')
      if (!mainContainer || !thumbContainer) return true
      const mainIds = [...mainContainer.querySelectorAll('.product-carousel__slide')].flatMap((slide) => {
        if (slide.classList.contains('product-carousel__slide--featured')) return []
        if (slide.classList.contains('product-carousel__slide--longform')) return []
        return slide.dataset.mediaId ? [slide.dataset.mediaId] : []
      })
      const thumbIds = [...thumbContainer.querySelectorAll('.product-carousel-thumbs__slide')].flatMap((slide) => {
        if (slide.classList.contains('product-carousel-thumbs__slide--featured')) return []
        return slide.dataset.mediaId ? [slide.dataset.mediaId] : []
      })
      return JSON.stringify(mainIds) === JSON.stringify(thumbIds)
    }

    destroyCarousels () {
      this.pauseAllCarouselVideos()
      Object.values(this.videoTimeouts).forEach(timeout => clearTimeout(timeout))
      this.videoTimeouts = {}
      if (this.mainEmbla) {
        this.mainEmbla.destroy()
        this.mainEmbla = null
      }
      if (this.thumbsEmbla) {
        this.thumbsEmbla.destroy()
        this.thumbsEmbla = null
      }
      this.removeThumbnailClickListeners()
      this.removeNavigationListeners()
    }

    async setupCarousels () {
      if (this.mainEmbla || this.thumbsEmbla) {
        this.destroyCarousels()
      }
      const mainViewport = this.querySelector('.product-carousel__viewport--main')
      if (!mainViewport) {
        console.error('Could not find main carousel viewport')
        return
      }
      this.mainEmbla = EmblaCarousel(mainViewport, this.mainOptions)
      let thumbViewport = null
      const isMobile = window.innerWidth < 1280
      if (this.hasLongformVideo && isMobile) {
        thumbViewport = this.querySelector('.product-carousel-thumbs__viewport-mobile')
      }
      if (!thumbViewport) {
        thumbViewport = this.querySelector('.product-carousel-thumbs__viewport')
      }
      if (!thumbViewport) {
        console.error('Could not find thumbnail carousel viewport')
        return
      }
      this.thumbsEmbla = EmblaCarousel(thumbViewport, this.thumbOptions)
      this.setupNavigation()
      const initialIndex = this.mainEmbla.selectedScrollSnap()
      this.controlSlideVideos(initialIndex)
      this.updateOverviewButtonState(initialIndex)
      this.updateSpinViewerVisibility(initialIndex)
      const initialThumbIndex = this.getThumbIndexForMainIndex(initialIndex)
      this.setSelectedThumbnail(initialThumbIndex)
      this.logCarouselDebug('setup-carousels-complete', { initialIndex, initialThumbIndex })
    }

    setupNavigation () {
      this.removeNavigationListeners()
      if (this.isCarouselDebug()) {
        this._debugSetupNavigationCount = (this._debugSetupNavigationCount || 0) + 1
      }
      const prevButtonNode = this.querySelector('.product-carousel__prev')
      const nextButtonNode = this.querySelector('.product-carousel__next')
      if (prevButtonNode) {
        prevButtonNode.addEventListener('click', this._handlePrevClick, false)
        this.navigationClickListeners.prev = { node: prevButtonNode, listener: this._handlePrevClick }
      }
      if (nextButtonNode) {
        nextButtonNode.addEventListener('click', this._handleNextClick, false)
        this.navigationClickListeners.next = { node: nextButtonNode, listener: this._handleNextClick }
      }
      this.mainEmbla.on('select', () => {
        const mainIndex = this.mainEmbla.selectedScrollSnap()
        this.logCarouselDebug('embla-select', {
          mainIndex,
          thumbIndex: this.getThumbIndexForMainIndex(mainIndex),
        })
        const thumbIndex = this.getThumbIndexForMainIndex(mainIndex)
        const thumbsNodes = this.thumbsEmbla.slideNodes()
        const mainSlide = this.mainEmbla.slideNodes()[mainIndex]
        const isLongformSlide = mainSlide?.classList.contains('product-carousel__slide--longform')
        if (isLongformSlide) {
          thumbsNodes.forEach(t => t.classList.remove('product-carousel-thumbs__slide--selected'))
        }
        if (window.innerWidth < 1280 && !isLongformSlide) {
          this.thumbsEmbla.scrollTo(thumbIndex, true)
        }
        if (thumbIndex < thumbsNodes.length && !isLongformSlide) {
          this.setSelectedThumbnail(thumbIndex)
        }
        this.updateOverviewButtonState(mainIndex)
        this.updateSpinViewerVisibility(mainIndex)
        this.controlSlideVideos(mainIndex)
      })
      this.handleKeydown = this.handleKeydown.bind(this)
      this.addEventListener('keydown', this.handleKeydown)
      if (!this.hasAttribute('tabindex')) {
        this.setAttribute('tabindex', '0')
      }
      this.logCarouselDebug('setup-navigation', { count: this._debugSetupNavigationCount || 0 })
    }

    pauseAllCarouselVideos () {
      Object.values(this.videoTimeouts).forEach(timeout => clearTimeout(timeout))
      Object.values(this.hintTimeouts).forEach(timeout => clearTimeout(timeout))
      this.videoTimeouts = {}
      this.hintTimeouts = {}
      this.querySelectorAll('video').forEach((video) => {
        video.pause()
      })
      this.querySelectorAll('product-gallery-video').forEach((productGalleryVideo) => {
        const video = productGalleryVideo.querySelector('video')
        if (video) {
          video.pause()
        }
        if (productGalleryVideo.videoPlayer) {
          productGalleryVideo.videoPlayer.isPlaying = false
          productGalleryVideo.videoPlayer.stopProgressUpdate?.()
          productGalleryVideo.videoPlayer.showPlayState?.()
        }
        if (typeof productGalleryVideo.pauseForSlideChange === 'function') {
          productGalleryVideo.pauseForSlideChange()
        } else if (typeof productGalleryVideo.pauseVideo === 'function') {
          productGalleryVideo.pauseVideo()
        }
      })
    }

    controlSlideVideos (activeIndex) {
      Object.values(this.videoTimeouts).forEach(timeout => clearTimeout(timeout))
      Object.values(this.hintTimeouts).forEach(timeout => clearTimeout(timeout))
      this.videoTimeouts = {}
      this.hintTimeouts = {}
      const slides = this.mainEmbla.slideNodes()
      slides.forEach((slide, index) => {
        const productGalleryVideo = slide.querySelector('product-gallery-video')
        const deferredMedia = slide.querySelector('deferred-media')
        const video = slide.querySelector('video')
        if (index === activeIndex && productGalleryVideo && productGalleryVideo.classList.contains('controls-hidden') === false) {
          const fsBtn = productGalleryVideo.querySelector('.fullscreen-btn')
          const hint = fsBtn ? fsBtn.querySelector('.fullscreen-hint') : null
          if (hint) {
            hint.classList.remove('opacity-100')
            hint.classList.add('opacity-0')
            this.hintTimeouts[`hint-show-${index}`] = setTimeout(() => {
              hint.classList.remove('opacity-0')
              hint.classList.add('opacity-100')
              this.hintTimeouts[`hint-hide-${index}`] = setTimeout(() => {
                hint.classList.remove('opacity-100')
                hint.classList.add('opacity-0')
              }, 6000)
            }, 2000)
          }
        }
        if (productGalleryVideo && typeof productGalleryVideo.loadVideo === 'function') {
          if (index === activeIndex) {
            if (productGalleryVideo.videoLoaded) {
              if (typeof productGalleryVideo.resumeForSlideChange === 'function') {
                productGalleryVideo.resumeForSlideChange()
              }
            } else if (!productGalleryVideo.isLoading) {
              productGalleryVideo.loadVideo()
            }
            const altText = productGalleryVideo.dataset.alt || ''
            const isLongformSlide = slide.classList.contains('product-carousel__slide--longform')
            if ((altText.includes('[longformAuto]') || isLongformSlide) && !productGalleryVideo.wasPausedByUser) {
              const attemptPlay = () => {
                if (productGalleryVideo.videoLoaded && typeof productGalleryVideo.playVideo === 'function') {
                  productGalleryVideo.playVideo()
                } else {
                  setTimeout(attemptPlay, 200)
                }
              }
              attemptPlay()
            }
          } else {
            if (typeof productGalleryVideo.pauseForSlideChange === 'function') {
              productGalleryVideo.pauseForSlideChange()
            }
          }
        } else if (video) {
          const slideId = `video-${index}`
          if (index === activeIndex) {
            window.Tentbox?.ensureLazyVideoSource?.(video)
            if (video.paused) {
              video.play()
            }
          } else {
            this.videoTimeouts[slideId] = setTimeout(() => {
              video.pause()
            }, 500)
          }
        }
        if (deferredMedia) {
          if (index === activeIndex && !deferredMedia.getAttribute('loaded')) {
            deferredMedia.loadContent(false)
          }
        }
      })
    }

    removeNavigationListeners () {
      if (this.navigationClickListeners.prev) {
        const { node, listener } = this.navigationClickListeners.prev
        node.removeEventListener('click', listener)
      }
      if (this.navigationClickListeners.next) {
        const { node, listener } = this.navigationClickListeners.next
        node.removeEventListener('click', listener)
      }
      this.navigationClickListeners = {}
    }

    setupThumbnailClick () {
      this.removeThumbnailClickListeners()
      const thumbsNodes = this.thumbsEmbla.slideNodes()
      thumbsNodes.forEach((thumb) => {
        const listener = () => {
          const mainIndex = this.getMainIndexForThumbElement(thumb)
          const thumbIndex = this.getThumbIndexForMainIndex(mainIndex)
          this.setSelectedThumbnail(thumbIndex)
          this.mainEmbla.scrollTo(mainIndex, true)
          if (thumbIndex < thumbsNodes.length) {
            this.thumbsEmbla.scrollTo(thumbIndex, true)
          }
          this.updateOverviewButtonState(mainIndex)
          this.updateSpinViewerVisibility(mainIndex)
        }
        thumb.addEventListener('click', listener)
        this.thumbnailClickListeners.push({ thumb, listener })
      })

      const mobileOverview = this.querySelector('.product-carousel-thumbs__mobile-overview')
      if (mobileOverview) {
        mobileOverview.querySelectorAll(':scope > .product-carousel-thumbs__slide--overview').forEach(overviewButton => {
          const overviewListener = () => {
            const mainIndex = this.getMainIndexForThumbElement(overviewButton)
            const thumbsNodes = this.thumbsEmbla.slideNodes()
            thumbsNodes.forEach(thumb => thumb.classList.remove('product-carousel-thumbs__slide--selected'))
            this.thumbsEmbla.scrollTo(0, true)
            this.mainEmbla.scrollTo(mainIndex, true)
            this.updateOverviewButtonState(mainIndex)
            this.updateSpinViewerVisibility(mainIndex)
          }
          overviewButton.addEventListener('click', overviewListener)
          this.thumbnailClickListeners.push({ thumb: overviewButton, listener: overviewListener })
        })
      }
    }

    handleFirstTimeLoad () {
      setTimeout(() => {
        this.reinitializeCarousel()
      }, 500)
    }

    removeThumbnailClickListeners () {
      this.thumbnailClickListeners.forEach(({ thumb, listener }) => {
        thumb.removeEventListener('click', listener)
      })
      this.thumbnailClickListeners = []
    }

    setSelectedThumbnail (index) {
      const thumbsNodes = this.thumbsEmbla.slideNodes()

      // Clear all thumbnail carousel slides (not overview buttons)
      thumbsNodes.forEach(t => t.classList.remove('product-carousel-thumbs__slide--selected'))
      const overviewButtons = this.querySelectorAll('.product-carousel-thumbs__slide--overview')
      overviewButtons.forEach(btn => btn.classList.remove('product-carousel-thumbs__slide--selected'))
      if (thumbsNodes[index]) {
        thumbsNodes[index].classList.add('product-carousel-thumbs__slide--selected')
      }
    }

    handlePrevClick () {
      if (!this.mainEmbla || this._mainNavLock) {
        this.logCarouselDebug('prev-blocked', { navLock: this._mainNavLock, hasEmbla: !!this.mainEmbla })
        return
      }
      if (this.isCarouselDebug()) {
        this._debugPrevClickCount = (this._debugPrevClickCount || 0) + 1
      }
      const before = this.mainEmbla.selectedScrollSnap()
      this.logCarouselDebug('prev-click-before', { clickNumber: this._debugPrevClickCount, before })
      this._mainNavLock = true
      this.mainEmbla.scrollPrev(true)
      const after = this.mainEmbla.selectedScrollSnap()
      this.logCarouselDebug('prev-click-after', {
        clickNumber: this._debugPrevClickCount,
        before,
        after,
        delta: after - before,
      })
      requestAnimationFrame(() => {
        this._mainNavLock = false
        this.logCarouselDebug('prev-click-settled')
      })
    }

    handleNextClick () {
      if (!this.mainEmbla || this._mainNavLock) {
        this.logCarouselDebug('next-blocked', { navLock: this._mainNavLock, hasEmbla: !!this.mainEmbla })
        return
      }
      if (this.isCarouselDebug()) {
        this._debugNextClickCount = (this._debugNextClickCount || 0) + 1
      }
      const before = this.mainEmbla.selectedScrollSnap()
      this.logCarouselDebug('next-click-before', { clickNumber: this._debugNextClickCount, before })
      this._mainNavLock = true
      this.mainEmbla.scrollNext(true)
      const after = this.mainEmbla.selectedScrollSnap()
      this.logCarouselDebug('next-click-after', {
        clickNumber: this._debugNextClickCount,
        before,
        after,
        delta: after - before,
        expectedAfter: (before + 1) % this.mainEmbla.slideNodes().length,
      })
      requestAnimationFrame(() => {
        this._mainNavLock = false
        this.logCarouselDebug('next-click-settled')
      })
    }

    handleKeydown (event) {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault()
        if (event.key === 'ArrowRight') {
          this.handleNextClick()
        } else if (event.key === 'ArrowLeft') {
          this.handlePrevClick()
        }
      }
    }

    getThumbSlideElements () {
      if (!this.thumbsEmbla) return []
      return [...this.thumbsEmbla.slideNodes()]
    }

    getThumbSlideElement (thumbEl) {
      if (!thumbEl) return null
      if (thumbEl.classList.contains('product-carousel-thumbs__slide')) return thumbEl
      return thumbEl.closest('.product-carousel-thumbs__slide')
    }

    getMainIndexForThumbElement (thumbEl) {
      const slide = this.getThumbSlideElement(thumbEl)
      if (!slide || !this.mainEmbla) return 0

      if (slide.classList.contains('product-carousel-thumbs__slide--featured')) {
        return 0
      }

      const mediaId = slide.dataset.mediaId
      if (mediaId) {
        const mainSlides = this.mainEmbla.slideNodes()
        const idx = mainSlides.findIndex(s => s.dataset?.mediaId === mediaId)
        if (idx >= 0) return idx
      }

      return 0
    }

    getThumbIndexForMainIndex (mainIndex) {
      if (!this.mainEmbla || !this.thumbsEmbla) return 0

      const mainSlide = this.mainEmbla.slideNodes()[mainIndex]
      if (!mainSlide) return 0

      const thumbs = this.getThumbSlideElements()

      if (mainSlide.classList.contains('product-carousel__slide--featured')) {
        const idx = thumbs.findIndex(t => t.classList.contains('product-carousel-thumbs__slide--featured'))
        return idx >= 0 ? idx : 0
      }

      const mediaId = mainSlide.dataset.mediaId
      if (mediaId) {
        const idx = thumbs.findIndex(t => t.dataset.mediaId === mediaId)
        if (idx >= 0) return idx
      }

      return Math.min(mainIndex, Math.max(thumbs.length - 1, 0))
    }

    getInitialThumbIndex () {
      return 0
    }

    getInitialMainIndex () {
      return 0
    }

    updateOverviewButtonState (mainIndex) {
      if (!this.hasLongformVideo) return
      const mainSlide = this.mainEmbla.slideNodes()[mainIndex]
      const isOnLongformSlide = mainSlide?.classList.contains('product-carousel__slide--longform')
      const mainMediaId = mainSlide?.dataset?.mediaId
      const overviewButtons = this.querySelectorAll('.product-carousel-thumbs__slide--overview')
      overviewButtons.forEach(button => {
        const matchesOverview = isOnLongformSlide && (
          !mainMediaId ||
          !button.dataset.mediaId ||
          button.dataset.mediaId === mainMediaId
        )
        if (matchesOverview) {
          button.classList.add('product-carousel-thumbs__slide--selected')
        } else {
          button.classList.remove('product-carousel-thumbs__slide--selected')
        }
      })
    }

    updateSpinViewerVisibility (mainIndex) {
      const mainSlide = this.mainEmbla.slideNodes()[mainIndex]
      // Covers both the pinned longform overview slide (`--longform` class) and any
      // additional longform-tagged video rendered inline in its natural position
      // (no `--longform` class, but still a controls-having `product-gallery-video`,
      // as opposed to a decorative autoplay `<video>`).
      const isLongformSlide = Boolean(
        mainSlide?.classList.contains('product-carousel__slide--longform') ||
        mainSlide?.querySelector('product-gallery-video'),
      )
      this.toggleAttribute('data-longform-active', isLongformSlide)
    }

    handleResize () {
      clearTimeout(this.resizeTimeout)
      this.resizeTimeout = setTimeout(() => {
        const currentViewport = window.innerWidth >= 1280 ? 'desktop' : 'mobile'
        const viewportChanged = this.lastViewport && this.lastViewport !== currentViewport
        if (viewportChanged && this.hasLongformVideo) {
          this.reinitializeCarousel()
        }
        this.lastViewport = currentViewport
      }, 150)
    }

    onVariantChange (event) {
      const { variant } = event.detail
      const variantId = String(variant.id)
      if (this._variantChangeFrame) cancelAnimationFrame(this._variantChangeFrame)
      this._variantChangeFrame = requestAnimationFrame(() => {
        this._variantChangeFrame = null
        this.applyVariantMediaChange(variantId)
      })
    }

    async applyVariantMediaChange (variantId) {
      this.logCarouselDebug('variant-change-start', { variantId })
      this.pauseAllCarouselVideos()
      await this.prepareVariantMedia(variantId)
      if (this.mainEmbla) {
        await this.reinitializeCarousel()
      } else {
        this._pendingVariantReinit = true
      }
      this.logCarouselDebug('variant-change-complete', { variantId })
    }

    removeAdditionalImages () {
      const additionalSlides = this.querySelectorAll('.product-carousel__slide--additional-image')
      additionalSlides.forEach(slide => slide.remove())
      const additionalThumbs = this.querySelectorAll('.product-carousel-thumbs__slide--additional-image')
      additionalThumbs.forEach(thumb => thumb.remove())
    }

    async addAdditionalImages (variantId, images) {
      const carouselContainer = this.querySelector('.product-carousel__container')
      const thumbsContainer = this.querySelector('.product-carousel-thumbs__container')
      const featuredImageSlide = carouselContainer.querySelector('.product-carousel__slide--featured')
      const featuredThumbSlide = thumbsContainer?.querySelector('.product-carousel-thumbs__slide--featured')
      const imageIndexOffset = 1
      let mainInsertAfter = featuredImageSlide
      let thumbInsertAfter = featuredThumbSlide
      images.forEach((image, index) => {
        const imageIndex = imageIndexOffset + index
        const slide = document.createElement('div')
        slide.classList.add('product-carousel__slide', 'product-carousel__slide--additional-image')
        slide.dataset.variantId = variantId
        slide.dataset.imageIndex = imageIndex
        slide.innerHTML = `<img src="${image}" class="w-full max-w-full rounded-sm bg-oat object-cover aspect-[4/3]" alt="Additional image">`
        if (mainInsertAfter) {
          mainInsertAfter.insertAdjacentElement('afterend', slide)
          mainInsertAfter = slide
        }
        const thumbSlide = document.createElement('div')
        thumbSlide.classList.add('product-carousel-thumbs__slide', 'product-carousel-thumbs__slide--additional-image')
        thumbSlide.dataset.variantId = variantId
        thumbSlide.dataset.imageIndex = imageIndex
        thumbSlide.innerHTML = `<button class="product-carousel-thumbs__slide-button rounded overflow-hidden block w-full h-full cursor-pointer" type="button" aria-label="View image ${imageIndex + 1}">
                                  <img src="${image}" class="w-full max-w-full rounded-sm bg-oat object-cover aspect-[4/3]" alt="Additional thumbnail">
                                </button>`
        if (thumbInsertAfter) {
          thumbInsertAfter.insertAdjacentElement('afterend', thumbSlide)
          thumbInsertAfter = thumbSlide
        }
      })
    }

    async reinitializeCarousel () {
      const run = async () => {
        this.logCarouselDebug('reinitialize-start')
        this.destroyCarousels()
        await this.setupCarousels()
        this.setupThumbnailClick()
        if (!this.mainEmbla || !this.thumbsEmbla) return
        this.mainEmbla.scrollTo(0, true)
        this.thumbsEmbla.scrollTo(0, true)
        const thumbIndex = this.getThumbIndexForMainIndex(0)
        this.setSelectedThumbnail(thumbIndex)
        this.updateOverviewButtonState(0)
        this.updateSpinViewerVisibility(0)
        this.logCarouselDebug('reinitialize-complete', { thumbIndex })
      }
      this._reinitializeChain = this._reinitializeChain.then(run, run)
      await this._reinitializeChain
    }

    disconnectedCallback () {
      Object.values(this.videoTimeouts).forEach(timeout => clearTimeout(timeout))
      this.videoTimeouts = {}
      this.destroyCarousels()
      this.removeEventListener('variantChange', this._onVariantChange)
      if (this._boundHandleResize) {
        window.removeEventListener('resize', this._boundHandleResize)
        this._boundHandleResize = null
      }
      if (this._variantChangeFrame) {
        cancelAnimationFrame(this._variantChangeFrame)
        this._variantChangeFrame = null
      }
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout)
        this.resizeTimeout = null
      }
      this._bootstrapPromise = null
    }

    set loading (value) {
      this.setAttribute('loading', value)
    }

    get loading () {
      return this.getAttribute('loading') === 'true'
    }

    get initialVariantId () {
      return this.dataset.initialVariantId
    }

    get featuredMedia () {
      return this.querySelectorAll('.product-media__variant-featured')
    }

    get variantImages () {
      return this.dataset.variantImages ? JSON.parse(this.dataset.variantImages) : {}
    }

    get variantSlugMap () {
      return this.dataset.variantSlugMap ? JSON.parse(this.dataset.variantSlugMap) : {}
    }

    get variantStash () {
      return this.querySelector('.product-media__variant-stash')
    }

    get hasLongformVideo () {
      return this.dataset.hasLongformVideo === 'true'
    }

    get longformVideoId () {
      return this.dataset.longformVideoId
    }

    get allLongformVideos () {
      return this.dataset.allLongformVideos ? this.dataset.allLongformVideos.split(',') : []
    }
  }
  customElements.define('product-media', ProductMedia)
}
/* eslint-enable  */
